/* eslint-disable import/no-named-as-default-member */
import { StateCreator } from 'zustand';
import chatApi from 'lib/api/chat';
import produce from 'immer';
import { TOTAL_ROOM_PER_PAGE } from 'features/chat/constant';
import { uniq } from 'utils/chat';
import { IChatStore, IChatAction, IRoomAction } from './types';
import { useGetChatUnreadCount } from './useGetChatUnreadCount';

const actions: StateCreator<
  IChatStore & IChatAction & IRoomAction,
  [],
  [],
  IRoomAction
> = (set, get) => ({
  fetchDefaultRooms: (params, sourceToken) =>
    new Promise((resolve, reject) => {
      const { offset, hasLoading = true } = params;

      set(
        produce((state: IChatStore) => {
          if (hasLoading) {
            if (offset === 0) {
              state.rooms.default.list = [];
              state.rooms.default.isLoading = true;
            } else {
              state.rooms.default.isLoadingMore = true;
            }
          }
        }),
      );

      chatApi
        .getRooms(params, sourceToken)
        .then((res) => {
          const { data, error } = res.data;

          set(
            produce((state: IChatStore) => {
              if (error) {
                state.rooms.default.isLoading = false;
                state.rooms.default.isLoadingMore = false;
              } else {
                if (hasLoading && offset !== 0) {
                  const concatRooms = uniq(
                    state.rooms.default.list.concat(data.rooms),
                    (rooms) => rooms.id,
                  );
                  state.rooms.default.list = concatRooms;
                } else {
                  state.rooms.default.list = data.rooms;
                }
                state.rooms.default.unreadRequest = data?.unread_request;
                state.rooms.default.totalInvited = data?.total_invited;
                state.rooms.default.pagination = data?.pagination;
              }

              if (offset === 0) {
                state.rooms.default.isLoading = false;
              } else {
                state.rooms.default.isLoadingMore = false;
              }
            }),
          );

          resolve(data);
        })
        .catch((err) => {
          set(
            produce((state: IChatStore) => {
              state.rooms.default.isLoading = false;
              state.rooms.default.isLoadingMore = false;
            }),
          );
          reject(err);
        });
    }),
  fetchInvitedRooms: (params) =>
    new Promise((resolve, reject) => {
      const { offset, hasLoading = true } = params;

      set(
        produce((state: IChatStore) => {
          if (offset === 0 && hasLoading) {
            state.rooms.invited.list = [];
            state.rooms.invited.isLoading = true;
          } else {
            state.rooms.invited.isLoadingMore = true;
          }
        }),
      );

      chatApi
        .getRequestedRooms(params)
        .then((res) => {
          const { data, error } = res.data;
          // on message request still using old calculation for pagination is_last_page
          const pagination = {
            is_last_page: data.rooms.length < TOTAL_ROOM_PER_PAGE,
          };

          const requestedData = {
            ...data,
            pagination,
          };

          set(
            produce((state: IChatStore) => {
              if (error) {
                state.rooms.invited.isLoading = true;
                state.rooms.default.isLoadingMore = false;
              } else if (hasLoading && Number(offset) > 0) {
                const concatRooms = uniq(
                  state.rooms.invited.list.concat(data.rooms),
                  (rooms) => rooms.id,
                );
                state.rooms.invited.list = concatRooms;
                state.rooms.invited.pagination = pagination;
              } else {
                state.rooms.invited.list = data.rooms;
                state.rooms.invited.pagination = pagination;
              }

              if (offset === 0) {
                state.rooms.invited.isLoading = false;
              } else {
                state.rooms.invited.isLoadingMore = false;
              }
            }),
          );

          resolve(requestedData);
        })
        .catch((err) => {
          set(
            produce((state: IChatStore) => {
              state.rooms.invited.isLoading = false;
              state.rooms.invited.isLoadingMore = false;
            }),
          );
          reject(err);
        });
    }),
  updateRoomListFromWS: (params) => {
    const newChat = {
      ...params,
      last_message: {
        ...params.last_message,
        // prevent missing text attributes on first time chat
        text: params?.last_message?.text || '',
      },
    };
    const isDefaultChat = newChat.state === 'ROOM_STATE_ACCEPTED';
    // decide where to place updated chat/room
    // rooms is for private, and requests is for request chat
    const stateBucket = isDefaultChat ? 'default' : 'invited';

    const { rooms } = get();
    const { fetchUnreadCount } = useGetChatUnreadCount.getState();

    const roomIdx = rooms[stateBucket].list.findIndex(
      (room) => room.id === newChat.id,
    );

    fetchUnreadCount();

    set(
      produce((state: IChatStore) => {
        const isRoomAvail = roomIdx > -1;
        const isUpdateRoomList =
          newChat?.last_message !==
          rooms[stateBucket]?.list[roomIdx]?.last_message;
        if (isUpdateRoomList) {
          if (isRoomAvail) {
            state.rooms[stateBucket].list.splice(roomIdx, 1);
          }
          state.rooms[stateBucket].list.unshift(newChat);
        }
      }),
    );
  },
  deleteChatRoom: (chatId) =>
    new Promise((resolve) => {
      chatApi
        .deleteChatRoom(chatId)
        .then((res) => {
          const { data } = res.data;
          const { room_id: roomId } = data;

          const defaultRoomsStoreData = get().rooms.default;

          set(
            produce((state: IChatStore) => {
              // remove all data related to `roomId`
              const roomIdx = defaultRoomsStoreData.list.findIndex(
                (room) => room.id === roomId,
              );

              if (roomIdx > -1) {
                const newChats = [...defaultRoomsStoreData.list];
                newChats.splice(roomIdx, 1);
                // rearrange chat list
                state.rooms.default.list = newChats;
              }

              if (state.conversation[roomId]) {
                delete state.conversation[roomId];
              }
            }),
          );

          resolve(data);
        })
        .catch(() => {});
    }),
  clearLastMessage: (chatId) => {
    const defaultRoomsStoreData = get().rooms.default;
    // remove last message
    const roomIdx = defaultRoomsStoreData.list.findIndex(
      (room) => Number(room.id) === Number(chatId),
    );

    set(
      produce((state: IChatStore) => {
        if (roomIdx > -1) {
          // update last message text to null
          state.rooms.default.list = Object.assign(
            [],
            defaultRoomsStoreData.list,
            {
              [roomIdx]: {
                ...defaultRoomsStoreData.list[roomIdx],
                last_message: {
                  ...defaultRoomsStoreData.list[roomIdx].last_message,
                  text: '',
                  sender_username: '',
                  type: 'ATTACHMENT_TYPE_UNSPECIFIED',
                  is_deleted: false,
                  id: 0,
                },
              },
            },
          );
        }
      }),
    );
  },
});

export default actions;
